import { useCallback } from 'react'

import { useQuery } from '@tanstack/react-query'

import { API_URL, IS_FEDRAMP_ENVIRONMENT } from '@/constants/env'
import { RELEVANCE_INDICES } from '@/constants/search/common'

import { isPast } from '@/helpers/date'
import { SweetspotError } from '@/helpers/error'

import { useIsTrialPeriod } from '@/hooks/billing/useIsTrialPeriod'
import {
  type OpportunitySearchResult,
  type OpportunityType,
  type RecommendedOpportunitiesList,
  newOpportunitiesQueryKeys,
} from '@/hooks/opportunity/types'
import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { UUID } from '@/types/Id'

export { OpportunitySearchResult, RecommendedOpportunitiesList }

interface FetchNewOpportunitiesQueryParams {
  limit?: number
  active_after?: string
  active_before?: string
  published_after?: string
  published_before?: string
  filter_sources?: OpportunityType[]
}

interface UseRecommendedOpportunitiesArgs<
  TData = RecommendedOpportunitiesList,
> {
  orgId: UUID | ''
  queryParams?: FetchNewOpportunitiesQueryParams
  enabled?: boolean
  select?: (data: RecommendedOpportunitiesList) => TData
}

export const useRecommendedOpportunities = <
  TData = RecommendedOpportunitiesList,
>({
  orgId,
  queryParams,
  enabled = true,
  select,
}: UseRecommendedOpportunitiesArgs<TData>) => {
  const { accessToken } = useAuthInfoWithThrow()

  return useQuery({
    queryKey: newOpportunitiesQueryKeys.organization({ orgId }),
    queryFn: () => fetchNewOpportunities(orgId, accessToken, queryParams),
    staleTime: 1000 * 60 * 60 * 24, // 24 hours,
    enabled: !IS_FEDRAMP_ENVIRONMENT && !!orgId && enabled,
    select,
  })
}

export const fetchNewOpportunities = async (
  orgId: UUID | '',
  accessToken: string,
  queryParams?: FetchNewOpportunitiesQueryParams,
): Promise<RecommendedOpportunitiesList> => {
  if (!orgId) {
    throw new Error('Organization ID is required')
  }
  let url = `${API_URL}/api/v1/enrichments/recommended-opportunities?organization_id=${orgId}`
  if (queryParams?.limit) {
    url += `&limit=${queryParams.limit}`
  }
  if (queryParams?.active_after) {
    url += `&active_after=${queryParams.active_after}`
  }
  if (queryParams?.active_before) {
    url += `&active_before=${queryParams.active_before}`
  }
  if (queryParams?.published_after) {
    url += `&published_after=${queryParams.published_after}`
  }
  if (queryParams?.published_before) {
    url += `&published_before=${queryParams.published_before}`
  }
  if (queryParams?.filter_sources) {
    url += `&${queryParams.filter_sources.join('&filter_sources=')}`
  }
  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
  })
  if (!response.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: 'Failed to fetch new opportunities',
      cause: response,
    })
  }
  return (await response.json()) as RecommendedOpportunitiesList
}

// =============================================================================
// Sorted/Filtered Hook with Business Logic
// =============================================================================

const getNDaysAgoAtMidnightUtc = (n: number) => {
  const date = new Date()
  date.setUTCHours(0, 0, 0, 0)
  date.setDate(date.getDate() - n)
  return date
}

interface UseFilteredRecommendedOpportunitiesArgs {
  maxCount?: number
}

interface FilteredOpportunitiesData {
  opportunities: OpportunitySearchResult[]
  hasAtLeastOneRelevanceThreshold: boolean
  numHighRelevance: number
  showNoHighlyRelevantWarning: boolean
}

export interface FilteredRecommendedOpportunities extends FilteredOpportunitiesData {
  isLoading: boolean
  trialExpired: boolean
}

// Stable empty array reference to avoid re-renders
const EMPTY_OPPORTUNITIES: OpportunitySearchResult[] = []

/**
 * Selector that sorts by relevance and computes all derived display states.
 */
const createFilteredOpportunitiesSelector = (maxCount: number) => {
  return (data: RecommendedOpportunitiesList): FilteredOpportunitiesData => {
    // Sort by relevance tier, then by posted date
    const sorted = [...data.data].sort((a, b) => {
      if (a.relevance_tier && b.relevance_tier) {
        if (a.relevance_tier === b.relevance_tier) {
          if (a.posted_date && b.posted_date) {
            return (
              new Date(b.posted_date).getTime() -
              new Date(a.posted_date).getTime()
            )
          }
          return 0
        }
        return (
          RELEVANCE_INDICES[a.relevance_tier] -
          RELEVANCE_INDICES[b.relevance_tier]
        )
      }
      return 0
    })

    // filter out opportunities that are past due
    const notPastDue = sorted.filter(
      (opp) => opp.active_until && !isPast(new Date(opp.active_until)),
    )

    const hasAtLeastOneRelevanceThreshold = notPastDue.some(
      (opp) => opp.relevance_tier !== undefined && opp.relevance_tier !== null,
    )

    const numHighRelevance = notPastDue.filter(
      (opp) => opp.relevance_tier === 'high',
    ).length

    return {
      opportunities: notPastDue.slice(0, maxCount),
      hasAtLeastOneRelevanceThreshold,
      numHighRelevance,
      showNoHighlyRelevantWarning:
        numHighRelevance === 0 && hasAtLeastOneRelevanceThreshold,
    }
  }
}

/**
 * Hook that provides recommended opportunities with all the business logic
 * for filtering, sorting, and deriving display states.
 */
export const useFilteredRecommendedOpportunities = ({
  maxCount = 5,
}: UseFilteredRecommendedOpportunitiesArgs = {}): FilteredRecommendedOpportunities => {
  const { activeOrgId } = useActiveOrgId()
  const { trialExpired, isLoading: isTrialPeriodLoading } = useIsTrialPeriod()
  const { user } = useAuthInfoWithThrow()

  // Hours since user was created - new users get more historical data
  const hoursSinceUserCreated = (Date.now() / 1000 - user.createdAt) / 60 / 60
  const isNewUser = hoursSinceUserCreated < 24

  const enabled = !(trialExpired || isTrialPeriodLoading)

  const selector = useCallback(createFilteredOpportunitiesSelector(maxCount), [
    maxCount,
  ])

  const { data, isLoading: isDataLoading } = useRecommendedOpportunities({
    orgId: activeOrgId,
    enabled,
    queryParams: {
      // New users get 28 days of opportunities since the daily pipeline
      // only runs once at 7am EST
      published_after: isNewUser
        ? getNDaysAgoAtMidnightUtc(28).toISOString()
        : undefined,
    },
    select: selector,
  })

  return {
    opportunities: data?.opportunities ?? EMPTY_OPPORTUNITIES,
    hasAtLeastOneRelevanceThreshold:
      data?.hasAtLeastOneRelevanceThreshold ?? false,
    numHighRelevance: data?.numHighRelevance ?? 0,
    showNoHighlyRelevantWarning: data?.showNoHighlyRelevantWarning ?? false,
    isLoading: isTrialPeriodLoading || isDataLoading,
    trialExpired: trialExpired ?? false,
  }
}
